import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'
import { MARKET_GROUPS, legKey, type MarketGroup, type QuotedOutcome, type Slip } from '@brochure/shared'
import { useGuest } from '@/hooks/useGuest'
import { useSlip } from '@/hooks/useSlip'
import { useFreshMatch, useZambeBetMatch, useWallet } from '@/hooks/useZambeBet'
import { LocalizedLink } from '@/components/LocalizedLink'
import { LoadError } from '@/components/zambepong/LoadError'
import { ScoreFlaps } from '@/components/zambepong/ScoreFlaps'
import { Toast, type ToastState } from '@/components/zambepong/Toast'
import { WalletCard } from '@/components/zambepong/TournamentHeader'
import { MatchMarkets } from '@/components/zambebet/MatchMarkets'
import { LiveSlips } from '@/components/zambebet/LiveSlips'
import { SlipBar, SlipDock } from '@/components/zambebet/SlipBar'
import { SlipSheet } from '@/components/zambebet/SlipSheet'
import { Skeleton } from '@/components/zambebet/Skeleton'
import { SideMark } from '@/components/zambepong/SideMark'
import { ScrollingName } from '@/components/ui/ScrollingName'
import { SegmentedTabs } from '@/components/ui/SegmentedTabs'
import { type DraftSlip, draftFromSlip, mergeDrafts, hasSelection, selectionCount, slipLimits, toggleSelection } from '@/lib/slip'
import { marketFilter, type Side } from '@/lib/zambebet'
import { phaseKey, phaseParams } from '@/lib/zambepong-format'

/**
 * I mercati di una partita, nella variante notte di ZambeBet, con la barra filtri da bookmaker (Principali,
 * Bicchieri, …; il filtro sta nell'indirizzo, di partenza i principali). Si chiede un gruppo per volta: cambiando
 * filtro restano intestazione e barra, i gruppi già visti arrivano subito dalla cache. Ogni esito aperto entra nella
 * stessa schedina della pagina ZambeBet (barra in basso, sheet dal basso); gli esiti decisi dal vivo mostrano spunta o croce.
 * Finché i dati non arrivano, la pagina c'è già con lo scheletro al posto di intestazione e mercati.
 */
export function ZambeBetMatchPage() {
  const { t } = useTranslation()
  const { id, matchId } = useParams<{ id: string; matchId: string }>()
  const { guest } = useGuest(id)
  const { slip, setSlip, place, isPlacing } = useSlip(id, guest?.token)
  const slipLegKeys = slip.legs.map(legKey)
  const [searchParams, setSearchParams] = useSearchParams()
  const filter = marketFilter(searchParams.get('filter'))
  const { detail, slipQuotes, mySlips, isLive } = useZambeBetMatch(id, matchId, filter, guest?.token, slipLegKeys)
  const storedWallet = useWallet(id, guest?.token)
  const freshMatch = useFreshMatch(id, guest?.token)
  const [isSlipOpen, setIsSlipOpen] = useState(false)
  const [toast, setToast] = useState<ToastState | null>(null)
  // Intestazione e barra: la risposta del gruppo scelto o, mentre arriva, quella del gruppo di prima (stessa partita).
  const shell = detail.data ?? (detail.previous?.match.matchId === matchId ? detail.previous : undefined)
  const groups = shell?.groups ?? []
  // Gruppo dell'indirizzo senza mercati in questa partita: si torna al primo che ne ha.
  const missing = detail.data !== undefined && groups.length > 0 && !groups.some((g) => g.key === filter)
  const fallback = groups[0]?.key ?? MARKET_GROUPS[0]

  function selectFilter(key: MarketGroup) {
    const next = new URLSearchParams(searchParams)
    if (key === MARKET_GROUPS[0]) next.delete('filter')
    else next.set('filter', key)
    setSearchParams(next, { replace: true })
  }

  // Solo quando il gruppo risulta mancante (selectFilter cambia a ogni render).
  useEffect(() => {
    if (missing) selectFilter(fallback)
  }, [missing, fallback])

  // Pagina intera d'errore solo senza nulla da mostrare; se fallisce un altro gruppo restano intestazione e barra.
  if (!shell && detail.error) {
    return (
      <main id="main" className="mx-auto max-w-7xl px-4 pt-28 pb-section sm:px-6 lg:px-8">
        <LoadError error={detail.error} className="text-ink">
          <LocalizedLink to="zambePongZambeBet" params={{ id: id! }} className="mt-4 inline-block font-body text-sm font-semibold text-sea-ink underline-offset-4 hover:underline">
            ← {t('zambebet.title')}
          </LocalizedLink>
        </LoadError>
      </main>
    )
  }
  // Risultato e stato sempre dall'ultima risposta arrivata, non dalla copia in cache di un altro gruppo.
  const match = shell?.match && freshMatch(shell.match)
  // Saldo unico per torneo e ospite: mai quello vecchio di una pagina in cache.
  const wallet = storedWallet !== undefined ? storedWallet : shell?.guest
  const players = shell?.players ?? { home: [], away: [] }
  const names: Record<string, string> = {
    ...(match && { home: match.homeTeam.name, away: match.awayTeam.name }),
    ...Object.fromEntries([...players.home, ...players.away].map((p) => [p.id, p.name])),
  }
  const sides: Record<string, Side> = Object.fromEntries([...players.home.map((p) => [p.id, 'home']), ...players.away.map((p) => [p.id, 'away'])])
  const limits = shell && slipLimits(shell.tournament, wallet?.balance ?? 0)

  function pick(o: QuotedOutcome) {
    const { odds } = o
    if (odds === null || !match) return
    setSlip((prev) =>
      toggleSelection(prev, { matchId: match.matchId, home: match.homeTeam.name, away: match.awayTeam.name }, {
        market: o.market,
        selection: o.selection,
        odds,
        ...(o.line !== undefined && { line: o.line }),
        ...(o.subjectId && { subjectId: o.subjectId, subjectName: names[o.subjectId] }),
      }),
    )
  }

  // Schedina svuotata dallo sheet (ultimo esito tolto o "Svuota"): lo sheet si chiude, non si riapre al prossimo tocco.
  function changeSlip(next: DraftSlip) {
    setSlip(next)
    if (selectionCount(next) === 0) setIsSlipOpen(false)
  }

  // "Rigioca con le nuove quote": la schedina annullata torna in bozza con le quote di allora (aggiunta a quella
  // eventualmente iniziata nel frattempo); lo sheet mostra "vecchia → nuova" e "Accetta le nuove quote e gioca".
  function replay(rejected: Slip) {
    setSlip((current) => mergeDrafts(current, draftFromSlip(rejected)))
    setIsSlipOpen(true)
  }

  // La bozza l'ha già svuotata `useSlip` (solo se non è cambiata durante l'invio). Una giocata live (`pending`)
  // non ha toast: la sua conferma la mostra la barra di `LiveSlips`, che compare con la rilettura delle schedine.
  function handlePlaced(placed: Slip) {
    setIsSlipOpen(false)
    if (placed.status !== 'pending') setToast({ tone: 'success', message: t('zambebet.slip.placed', { count: placed.totalStake }) })
    detail.reload()
    mySlips.reload()
  }

  return (
    <>
      <div aria-hidden="true" className="h-16 bg-paper md:h-20" />
      <main id="main" className="night min-h-dvh">
        <div className="mx-auto max-w-2xl px-4 pt-4 pb-section sm:px-6 lg:max-w-4xl">
          <LocalizedLink
            to="zambePongZambeBet"
            params={{ id: id! }}
            className="-ml-2 inline-flex min-h-11 items-center gap-1.5 rounded-full px-2 font-body text-sm font-semibold text-paper/65 transition-colors hover:bg-paper/10 hover:text-paper"
          >
            <ArrowLeft className="size-4 shrink-0" aria-hidden="true" />
            {t('zambebet.title')}
          </LocalizedLink>

          {match ? (
            <header className="mt-3 rounded-2xl border border-paper/12 bg-paper/[0.04] p-4" data-testid="sb-match-header">
              <p className="flex items-center justify-between gap-3 font-body text-xs font-semibold text-paper/60">
                {t(phaseKey(match.phase), phaseParams(match.phase))}
                {match.status === 'live' && isLive && (
                  <span className="flex items-center gap-1 rounded-full bg-coral px-1.5 py-px text-xs font-bold tracking-[0.02em] text-ink">
                    <span className="size-1 rounded-full bg-ink motion-safe:animate-pulse" aria-hidden="true" />
                    {t('zambePong.live')}
                  </span>
                )}
              </p>
              <h1 aria-label={`${match.homeTeam.name} – ${match.awayTeam.name}`} className="mt-3 flex items-center gap-3">
                <SideMark side="home" className="h-5" />
                <ScrollingName className="flex-1 text-right font-body tabular-nums text-xl font-medium tracking-tight text-paper">{match.homeTeam.name}</ScrollingName>
                <span className="shrink-0 rounded-lg bg-paper/5 px-2 py-1">
                  <ScoreFlaps home={match.homeScore} away={match.awayScore} className="text-xl text-paper" />
                </span>
                <ScrollingName className="flex-1 font-body tabular-nums text-xl font-medium tracking-tight text-paper">{match.awayTeam.name}</ScrollingName>
                <SideMark side="away" className="h-5" />
              </h1>
              <LocalizedLink
                to="zambePongMatch"
                params={{ id: id!, matchId: match.matchId }}
                className="mx-auto mt-3 flex min-h-11 w-fit items-center font-body text-sm font-semibold text-sun underline-offset-4 hover:underline"
              >
                {t('zambebet.match.followLive')}
              </LocalizedLink>
            </header>
          ) : (
            <Skeleton className="mt-3 h-[9.5rem] rounded-2xl" />
          )}

          {wallet && <WalletCard name={wallet.name} balance={wallet.balance} className="mt-4" />}
          {match?.blockedBy === 'ownTeam' && (
            <p className="mt-4 font-body text-sm text-paper/70" data-testid="sb-match-own">
              {t('zambebet.blocked.ownTeam')}
            </p>
          )}

          {shell && groups.length === 0 ? (
            <p className="mt-5 font-body text-paper/60">{t('zambebet.match.noMarkets')}</p>
          ) : (
            <>
              {/* Fascia sticky sotto l'header del sito, come le tab della pagina ZambeBet. */}
              <div className="sticky top-16 z-20 -mx-4 mt-5 bg-ink/95 px-4 py-2 backdrop-blur-md sm:-mx-6 sm:px-6 md:top-20">
                {shell ? (
                  <SegmentedTabs
                    tabs={groups.map((g) => ({ key: g.key, label: `${t(`zambebet.groups.${g.key}`)} ${g.count}` }))}
                    active={filter}
                    onSelect={selectFilter}
                    ariaLabel={t('zambebet.filters.ariaLabel')}
                    idPrefix="sb-filter"
                    panelId="sb-filter-panel"
                    testIdPrefix="sb-filter-"
                    tone="night"
                  />
                ) : (
                  <Skeleton className="h-11 rounded-full" />
                )}
              </div>
              <div role="tabpanel" id="sb-filter-panel" aria-labelledby={shell ? `sb-filter-${filter}` : undefined} aria-busy={!detail.data} className="mt-4">
                {detail.data && match ? (
                  <MatchMarkets
                    markets={detail.data.markets}
                    group={filter}
                    names={names}
                    sides={sides}
                    canPick={match.canBet && !!guest}
                    isPicked={(o) => hasSelection(slip, match.matchId, o)}
                    onPick={pick}
                  />
                ) : detail.error ? (
                  <div data-testid="sb-match-error" role="status" className="flex flex-col items-start gap-3 font-body text-sm text-paper/80">
                    {t('zambebet.match.loadError')}
                    <button type="button" onClick={detail.reload} className="min-h-11 rounded-full border border-paper/30 px-5 font-semibold text-paper transition-colors hover:bg-paper/10">
                      {t('zambebet.slip.retry')}
                    </button>
                  </div>
                ) : (
                  <div data-testid="sb-match-skeleton" className="space-y-2">
                    {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-24" />)}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {wallet && guest && limits && (
          <>
            <SlipDock>
              <LiveSlips data={mySlips.data} onReplay={replay} />
              <SlipBar slip={slip} quotes={slipQuotes.data} limits={limits} onOpen={() => setIsSlipOpen(true)} />
            </SlipDock>
            {isSlipOpen && (
              <SlipSheet
                place={place}
                isPlacing={isPlacing}
                slip={slip}
                quotes={slipQuotes.data}
                quotesError={slipQuotes.error}
                onRetryQuotes={slipQuotes.reload}
                limits={limits}
                onChange={changeSlip}
                onClose={() => setIsSlipOpen(false)}
                onPlaced={handlePlaced}
                onStale={slipQuotes.reload}
              />
            )}
          </>
        )}
        {toast && <Toast toast={toast} onDismiss={() => setToast(null)} />}
      </main>
    </>
  )
}
